"use client";

import { GetChatMessagesResponseDTO } from "@/app/api/chats/[chatId]/messages/messages.dto";
import LoadingMessages from "@/components/shared/loading-messages";
import { BASE_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import ChatEmptyState from "./components/chat-empty-state";
import ChatHeader from "./components/chat-header";
import ChatInput from "./components/chat-input";
import Message from "./components/message";

const fetchMessages = async (uniqueChatId?: string) => {
	try {
		if (!uniqueChatId) {
			return [];
		}

		const url = new URL(`${BASE_URL}/api/chats/${uniqueChatId}/messages`);
		const response = await fetch(url, { next: { tags: ["messages"] } });

		if (!response.ok) {
			return [];
		}

		const { data } = (await response.json()) as GetChatMessagesResponseDTO;

		return data;
	} catch (error) {
		return [];
	}
};

const Chat = ({ chatId }: { chatId: string | undefined }) => {
	const {
		isLoading,
		isError,
		error,
		data: messages,
	} = useQuery({
		queryKey: ["chatMessages", chatId],
		queryFn: () => fetchMessages(chatId),
		enabled: !!chatId,
		refetchInterval: 20000,
	});

	const scrollRef = useRef<HTMLDivElement | null>(null);

	const [hasScrolled, setHasScrolled] = useState<boolean>(false);

	const [scrollToBottom, setScrollToBottom] = useState<boolean>(true);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		e.stopPropagation();

		const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;

		if (scrollHeight - scrollTop > clientHeight + 300) {
			setHasScrolled(true);
		} else {
			setHasScrolled(false);
		}

		setScrollToBottom(scrollHeight - scrollTop === clientHeight);
	};

	const scrollToBottomIfNeeded = useCallback(() => {
		if (scrollToBottom && scrollRef.current) {
			scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
		}
	}, [scrollToBottom]);

	// Scroll to bottom on mount
	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, []);

	// Scroll to bottom on new messages
	useEffect(() => {
		scrollToBottomIfNeeded();
	}, [messages, scrollToBottomIfNeeded]);

	return (
		<div className="max-w- h-full rounded-lg bg-white shadow-sm">
			{!chatId ? (
				<ChatEmptyState
					title="Welcome to Your Chat Inbox"
					description="Select a conversation to start chatting."
				/>
			) : (
				<div>
					<ChatHeader />

					<div
						ref={scrollRef}
						onScroll={handleScroll}
						className="customized-scrollbar h-[calc(100vh-330px)] grow space-y-4 overflow-y-scroll bg-[url('/images/chat/background.svg')] bg-cover bg-no-repeat p-4"
					>
						<button
							type="button"
							onClick={() => {
								if (scrollRef.current) {
									scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
									setScrollToBottom(true);
								}
							}}
							className={cn(
								"sticky left-1/2 top-[-4px] z-10 w-fit translate-x-[-50%] transform items-center justify-center gap-1 rounded-full bg-yellow-500 px-4 py-2.5 font-semibold text-slate-900 opacity-80 transition-all duration-300 ease-linear hover:opacity-100",
								hasScrolled ? "flex" : "hidden",
								"text-xxs"
							)}
						>
							<span>Scroll to new messages</span>
							<span>
								<ArrowDownIcon className="h-3 w-3" />
							</span>
						</button>

						{isLoading ? (
							<LoadingMessages />
						) : isError ? (
							<span>{JSON.stringify(error)}</span>
						) : messages && messages.length > 0 ? (
							messages?.map((message) => <Message key={message.id} data={message} />)
						) : (
							<ChatEmptyState />
						)}
					</div>

					<div className="h-fit border-t border-gray-100 p-2.5">
						<ChatInput />
					</div>
				</div>
			)}
		</div>
	);
};

export default Chat;
